import { useEffect, useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { useFormikContext } from "formik"
import { Stack, Typography } from "@mui/material"
import { DeliveryTimesField, DynamicConfigKey, MeetingServiceTabValues } from "../../form/formTypes"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { MeetingServiceTimeErrorDialog } from "./MeetingServiceTimeErrorDialog"
import { MeetingServicesPlaceholder } from "./MeetingServicesPlaceholder"
import { useMoney } from "../../../../../../modules/currency/useMoney"
import { MeetingServiceDelivery } from "./MeetingServiceDelivery"
import { calculateTotalAmountFromDeliveries } from "./helpers"
import { INVALID_DELIVERIES } from "../../form/helpers"
import { OrderSummaryItem } from "./OrderSummaryItem"

const useStyles = makeStyles()((theme) => ({
    footerInfo: {
        fontWeight: 400,
        color: theme.palette.text.secondary,
    },
}))

const defaultDialogState = {
    show: false,
    times: [],
}

export type ProductUpdateQuantityProps = Readonly<{
    deliveryTime: string
    productId: number
    quantity: number
    isExtraService?: boolean
}>

type MeetingServicesProps = Readonly<{
    isRefundOrder: boolean
    isEditable: boolean
}>

export function MeetingServices({ isRefundOrder, isEditable }: MeetingServicesProps) {
    const moneyFactory = useMoney({})
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    const { values, setFieldValue, errors, isSubmitting } = useFormikContext<MeetingServiceTabValues>()
    const deliveries: DeliveryTimesField | undefined = values[DynamicConfigKey.MULTIPLE_DELIVERIES]
    const deliveryTimes = deliveries?.deliveryTimes ?? []
    const deliveryDate = deliveries?.deliveryDate ?? null
    const invalidTimes = errors[DynamicConfigKey.MULTIPLE_DELIVERIES]

    const [showInvalidTimesModal, setShowInvalidTimesModal] = useState<{ show: boolean; times: string[] }>(
        defaultDialogState,
    )

    useEffect(() => {
        if (isSubmitting && typeof invalidTimes === "string" && invalidTimes.startsWith(INVALID_DELIVERIES)) {
            const times = invalidTimes.replace(INVALID_DELIVERIES, "").split(",") || []
            if (times.length > 0) {
                setShowInvalidTimesModal({
                    show: true,
                    times: times,
                })
            }
        }
    }, [isSubmitting, invalidTimes])

    const onUpdateProductQuantity = ({
        deliveryTime,
        productId,
        quantity,
        isExtraService,
    }: ProductUpdateQuantityProps) => {
        const updatedDeliveries = {
            ...deliveries,
            deliveryTimes: deliveryTimes.map((delivery) => {
                if (delivery.time === deliveryTime) {
                    const current = delivery.orderLines.findIndex(
                        (item) => item.id === productId && item.isExtraService === isExtraService,
                    )
                    let newOrderLines = [...delivery.orderLines]
                    if (quantity > 0 || isRefundOrder) {
                        if (current !== -1) {
                            newOrderLines[current] = { ...newOrderLines[current], quantity }
                        }
                    } else {
                        newOrderLines = newOrderLines.filter(
                            (item) => !(item.id === productId && item.isExtraService === isExtraService),
                        )
                    }
                    return { ...delivery, orderLines: newOrderLines }
                }
                return delivery
            }),
        }
        setFieldValue(DynamicConfigKey.MULTIPLE_DELIVERIES, updatedDeliveries)
    }

    const totalAmount = useMemo(
        () => (deliveries ? calculateTotalAmountFromDeliveries(deliveries) : null),
        [deliveryTimes],
    )

    const formattedDate = deliveryDate && dateTimeFactory.fromISO(deliveryDate).toFormat("d MMMM")
    const totalProductsCount = deliveryTimes.reduce((sum, delivery) => sum + delivery.orderLines.length, 0)
    const hasProducts = totalProductsCount > 0

    const handleDialogClose = () => {
        if (showInvalidTimesModal.times.length > 0) {
            const firstInvalidTime = showInvalidTimesModal.times[0]
            setFieldValue(`${DynamicConfigKey.MULTIPLE_DELIVERIES}.activeTime`, firstInvalidTime)
        }
        setShowInvalidTimesModal(defaultDialogState)
    }

    return (
        <LocalizedStrict id="create-order-order-summary-meeting-services" attrs={{ title: true }}>
            <OrderSummaryItem title="Meeting services">
                <Stack gap="1.5rem">
                    {formattedDate ? (
                        deliveryTimes.map((delivery, index) => (
                            <MeetingServiceDelivery
                                key={index}
                                serialNumber={index + 1}
                                deliveryTime={delivery.time || ""}
                                deliveryDate={deliveryDate}
                                orderLines={delivery.orderLines}
                                isRefundOrder={isRefundOrder}
                                onUpdateQuantity={onUpdateProductQuantity}
                                isEditable={isEditable}
                            />
                        ))
                    ) : (
                        <MeetingServicesPlaceholder />
                    )}
                    {hasProducts && (
                        <Stack flexDirection="row" gap="1rem" justifyContent="space-between" alignItems="flex-end">
                            <LocalizedStrict id="create-order-order-summary-meeting-services-total">
                                <Typography variant="h6" className={classes.footerInfo}>
                                    Total
                                </Typography>
                            </LocalizedStrict>
                            <Typography variant="h4">
                                {isRefundOrder && "-"}
                                {totalAmount && moneyFactory.format(totalAmount)}
                            </Typography>
                        </Stack>
                    )}

                    {showInvalidTimesModal.show && (
                        <MeetingServiceTimeErrorDialog
                            show={showInvalidTimesModal.show}
                            times={showInvalidTimesModal.times}
                            handleDialogClose={handleDialogClose}
                        />
                    )}
                </Stack>
            </OrderSummaryItem>
        </LocalizedStrict>
    )
}
